import React, { useEffect, useMemo, useRef, useState } from "react";
import { collection, onSnapshot, addDoc, serverTimestamp } from "firebase/firestore";
import emailjs from "@emailjs/browser";
import { db } from "../firebase";
import {
  uploadDocumentToCloudinary,
  validateDocument,
  MAX_FILE_MB,
} from "../utils/uploadFile";
import "./CareersPage.css";
import "./CareersMotion.css";

const SCHOOL = {
  name: "Academic Group of Schools & College",
  email: "academicgroupofschools@gmail.com",
  phone: "+92 304 7630462",
  address: "Chowk Shah Abbas, Suraj Kund Road, Multan",
};

// EmailJS settings (emailjs.com se bharein)
const EMAILJS = {
  serviceId: "YOUR_SERVICE_ID",
  templateId: "YOUR_TEMPLATE_ID",
  publicKey: "YOUR_PUBLIC_KEY",
};

const CATEGORIES = ["All", "Teaching", "Administration", "Support Staff"];
const EMPTY_FORM = { name: "", email: "", phone: "", message: "" };
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isEmailConfigured = () =>
  !EMAILJS.serviceId.startsWith("YOUR_") &&
  !EMAILJS.templateId.startsWith("YOUR_") &&
  !EMAILJS.publicKey.startsWith("YOUR_");

const ts = (v) => v?.toDate?.()?.getTime?.() ?? 0;

const formatDate = (v) =>
  v?.toDate
    ? v.toDate().toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
    : "";

const formatSize = (bytes) =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

// ─── Icons (inline SVG) ───
const ICON_PATHS = {
  search: (<><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>),
  pin: (<><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></>),
  briefcase: (<><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></>),
  calendar: (<><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>),
  upload: (<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></>),
  doc: (<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></>),
  check: (<polyline points="20 6 9 17 4 12" />),
  close: (<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>),
  mail: (<><rect x="2" y="4" width="20" height="16" rx="2" /><polyline points="22 6 12 13 2 6" /></>),
  phone: (<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />),
  arrow: (<><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></>),
};

const Icon = ({ name, size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {ICON_PATHS[name]}
  </svg>
);

const CareersPage = () => {
  const pageRef = useRef(null);

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");

  // Application modal
  const [activeJob, setActiveJob] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [cvFile, setCvFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [status, setStatus] = useState(null);

  // Live jobs from Firestore (admin panel se add hoti hain)
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "careers"),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => ts(b.createdAt) - ts(a.createdAt));
        setJobs(list);
        setLoading(false);
      },
      (err) => {
        console.error("Careers:", err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const filteredJobs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return jobs.filter((j) => {
      const matchesCat = category === "All" || j.category === category;
      const matchesText =
        !q ||
        j.title?.toLowerCase().includes(q) ||
        j.department?.toLowerCase().includes(q) ||
        j.description?.toLowerCase().includes(q);
      return matchesCat && matchesText;
    });
  }, [jobs, search, category]);

  // Scroll reveal.
  // Root par "is-armed" class tabhi lagti hai jab ye JS chal jaye, is liye
  // JS band ho ya load na ho to content kabhi chhupa nahi rahega.
  // Fallback: 1.5 second baad sab elements khud dikh jate hain.
  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    root.classList.add("is-armed");

    const reveal = (el) => el.classList.add("is-in");
    const pending = root.querySelectorAll("[data-reveal]:not(.is-in)");

    if (!("IntersectionObserver" in window)) {
      pending.forEach(reveal);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            reveal(en.target);
            io.unobserve(en.target);
          }
        });
      },
      { threshold: 0.05, rootMargin: "0px 0px 60px 0px" }
    );
    pending.forEach((el) => io.observe(el));

    const fallback = setTimeout(() => {
      root.querySelectorAll("[data-reveal]").forEach(reveal);
    }, 1500);

    return () => {
      clearTimeout(fallback);
      io.disconnect();
    };
  }, [loading, search, category, filteredJobs.length]);

  // Modal: Escape se band, background scroll lock
  useEffect(() => {
    if (!activeJob) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) setActiveJob(null);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [activeJob, submitting]);

  // ─── 3D tilt (sirf mouse wale devices par) ───
  const tiltMove = (e) => {
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const el = e.currentTarget;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    el.style.setProperty("--rx", `${(0.5 - py) * 9}deg`);
    el.style.setProperty("--ry", `${(px - 0.5) * 11}deg`);
    el.style.setProperty("--gx", `${px * 100}%`);
    el.style.setProperty("--gy", `${py * 100}%`);
  };
  const tiltLeave = (e) => {
    const el = e.currentTarget;
    el.style.setProperty("--rx", "0deg");
    el.style.setProperty("--ry", "0deg");
  };
  const tiltProps = { onMouseMove: tiltMove, onMouseLeave: tiltLeave };

  const countFor = (c) =>
    c === "All" ? jobs.length : jobs.filter((j) => j.category === c).length;

  // Modal helpers
  const openApply = (job) => {
    setActiveJob(job);
    setForm(EMPTY_FORM);
    setCvFile(null);
    setStatus(null);
    setDone(false);
  };
  const closeApply = () => {
    if (submitting) return;
    setActiveJob(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFile = (e) => {
    setCvFile(e.target.files?.[0] || null);
    setStatus(null);
  };

  const sendNotification = (params) => {
    if (!isEmailConfigured()) return null;
    return emailjs.send(EMAILJS.serviceId, EMAILJS.templateId, params, EMAILJS.publicKey);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setStatus({ type: "error", text: "Please enter your name." });
    if (!EMAIL_REGEX.test(form.email.trim()))
      return setStatus({ type: "error", text: "Please enter a valid email address.." });
    if (!cvFile) return setStatus({ type: "error", text: "Please select your CV.." });
    const problem = validateDocument(cvFile);
    if (problem) return setStatus({ type: "error", text: problem });

    try {
      setSubmitting(true);
      setStatus(null);

      // 1. CV upload (Cloudinary)
      const { url } = await uploadDocumentToCloudinary(cvFile);

      // 2. Application save (admin panel ke Applications tab mein dikhegi)
      await addDoc(collection(db, "applications"), {
        jobId: activeJob.id,
        jobTitle: activeJob.title || "",
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
        cvUrl: url,
        cvName: cvFile.name,
        createdAt: serverTimestamp(),
      });

      // 3. School email (alag try/catch: fail ho to bhi application save rahegi)
      try {
        const mail = sendNotification({
          to_email: SCHOOL.email,
          job_title: activeJob.title || "",
          applicant_name: form.name.trim(),
          applicant_email: form.email.trim(),
          applicant_phone: form.phone.trim() || "Not provided",
          message: form.message.trim() || "—",
          cv_link: url,
        });
        if (mail) await mail;
      } catch (mailErr) {
        console.error("Email failed:", mailErr);
      }

      setForm(EMPTY_FORM);
      setCvFile(null);
      setDone(true);
    } catch (err) {
      console.error("Application error:", err);
      const reason = err?.code || err?.message || "unknown";
      setStatus({
        type: "error",
        text: `There was an issue submitting the application. (${reason}). Please try again.`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cr-page" ref={pageRef}>
      {/* ───────── Hero ───────── */}
      <header className="cr-hero">
        <span className="cr-orb cr-orb--a" aria-hidden="true" />
        <span className="cr-orb cr-orb--b" aria-hidden="true" />
        <span className="cr-orb cr-orb--c" aria-hidden="true" />
        <div className="cr-wrap">
          <span className="cr-eyebrow cr-rise" style={{ "--d": 0 }}>Careers</span>
          <h1 className="cr-rise" style={{ "--d": 1 }}>Join Our Team</h1>
          <p className="cr-rise" style={{ "--d": 2 }}>
            Be part of excellence in education. Shape the future of our students with us.
          </p>
          <div className="cr-hero-stats cr-rise" style={{ "--d": 3 }}>
            <span className="cr-hero-stat">
              {loading ? "Loading..." : `${jobs.length} open position${jobs.length !== 1 ? "s" : ""}`}
            </span>
            <span className="cr-hero-stat">Apply online in minutes</span>
          </div>
        </div>
      </header>

      <main className="cr-wrap">
        {/* ───────── Toolbar ───────── */}
        <section className="cr-toolbar" aria-label="Search and filter positions" data-reveal>
          <label className="cr-search">
            <Icon name="search" size={18} />
            <input
              type="search"
              placeholder="Search by title, department or keyword"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search positions"
            />
          </label>

          <div className="cr-chips" role="tablist" aria-label="Filter by category">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                role="tab"
                aria-selected={category === c}
                className={`cr-chip ${category === c ? "is-active" : ""}`}
                onClick={() => setCategory(c)}
              >
                <span className="cr-chip-label">{c}</span>
                <span className="cr-chip-count">{countFor(c)}</span>
              </button>
            ))}
          </div>
        </section>

        {/* ───────── Results header ───────── */}
        <div className="cr-results">
          <h2>Open Positions</h2>
          {!loading && (
            <p className="cr-count-text">
              Showing {filteredJobs.length} of {jobs.length}
            </p>
          )}
        </div>

        {/* ───────── Loading skeleton ───────── */}
        {loading && (
          <div className="cr-grid">
            {[0, 1, 2].map((i) => (
              <div key={i} className="cr-skel" aria-hidden="true">
                <div className="cr-skel-row">
                  <div className="cr-skel-avatar" />
                  <div className="cr-skel-lines">
                    <div className="cr-skel-line w-70" />
                    <div className="cr-skel-line w-40" />
                  </div>
                </div>
                <div className="cr-skel-line w-100" />
                <div className="cr-skel-line w-90" />
                <div className="cr-skel-line w-60" />
              </div>
            ))}
          </div>
        )}

        {/* ───────── Job cards ───────── */}
        {!loading && filteredJobs.length > 0 && (
          <div className="cr-grid">
            {filteredJobs.map((job, index) => (
              <div
                key={job.id}
                className="cr-reveal"
                data-reveal
                style={{ "--i": Math.min(index, 8) }}
              >
                <article className="cr-card cr-tilt" {...tiltProps}>
                  <span className="cr-glare" aria-hidden="true" />

                  <div className="cr-card-top">
                    <div className="cr-avatar" aria-hidden="true">
                      {job.title?.charAt(0)?.toUpperCase() || "J"}
                    </div>
                    <div className="cr-card-head">
                      <h3 className="cr-card-title">{job.title}</h3>
                      {job.department && <p className="cr-card-dept">{job.department}</p>}
                    </div>
                    {job.category && (
                      <span className="cr-badge" data-cat={job.category}>
                        {job.category}
                      </span>
                    )}
                  </div>

                  {job.description && <p className="cr-desc">{job.description}</p>}

                  <ul className="cr-meta">
                    <li className="cr-meta-item">
                      <Icon name="briefcase" size={16} />
                      <span><b>Experience:</b> {job.experience || "Not specified"}</span>
                    </li>
                    <li className="cr-meta-item">
                      <Icon name="pin" size={16} />
                      <span><b>Location:</b> {job.location || SCHOOL.address}</span>
                    </li>
                    <li className="cr-meta-item">
                      <Icon name="calendar" size={16} />
                      <span><b>Posted:</b> {formatDate(job.createdAt) || "Recently"}</span>
                    </li>
                  </ul>

                  <button type="button" className="cr-apply" onClick={() => openApply(job)}>
                    <span>Apply Now</span>
                    <Icon name="arrow" size={16} />
                  </button>
                </article>
              </div>
            ))}
          </div>
        )}

        {/* ───────── Empty state ───────── */}
        {!loading && filteredJobs.length === 0 && (
          <div className="cr-empty" data-reveal>
            <div className="cr-empty-icon">
              <Icon name="briefcase" size={28} />
            </div>
            <h3>{jobs.length === 0 ? "No open positions right now" : "No matching positions"}</h3>
            <p>
              {jobs.length === 0
                ? "No positions are currently available. You can send your CV to the email address provided below."
                :  "Please adjust your search or filters and try again."}
            </p>
          </div>
        )}

        {/* ───────── CTA ───────── */}
        <section className="cr-cta" data-reveal>
          <span className="cr-cta-ring cr-cta-ring--1" aria-hidden="true" />
          <span className="cr-cta-ring cr-cta-ring--2" aria-hidden="true" />
          <div className="cr-cta-text">
            <span className="cr-cta-kicker">Always Open</span>
            <h2>Don't see your role?</h2>
            <p>Send your CV and cover letter to us. We will contact you when a suitable position opens.</p>
          </div>
          <div className="cr-cta-actions">
            <a href={`mailto:${SCHOOL.email}?subject=Job%20Application`} className="cr-btn cr-btn--gold">
              <Icon name="mail" size={18} />
              <span>Email Your CV</span>
            </a>
          </div>
        </section>

     
        {/* ───────── Contact ───────── */}
<section className="cr-contact" aria-label="Contact information">
  <div className="cr-contact-card cr-contact-card--address" data-reveal style={{ "--i": 0 }}>
    <span className="cr-ico"><Icon name="pin" size={20} /></span>
    <div className="cr-contact-body">
      <span className="cr-contact-label">Address</span>
      <span className="cr-contact-value">{SCHOOL.address}</span>
    </div>
  </div>

  <a
    className="cr-contact-card cr-contact-card--email"
    href={`mailto:${SCHOOL.email}`}
    data-reveal
    style={{ "--i": 1 }}
  >
    <span className="cr-ico"><Icon name="mail" size={20} /></span>
    <div className="cr-contact-body">
      <span className="cr-contact-label">Email</span>
      <span className="cr-contact-value cr-contact-value--email">{SCHOOL.email}</span>
    </div>
  </a>

  <a
    className="cr-contact-card cr-contact-card--phone"
    href={`tel:${SCHOOL.phone.replace(/\s/g, "")}`}
    data-reveal
    style={{ "--i": 2 }}
  >
    <span className="cr-ico"><Icon name="phone" size={20} /></span>
    <div className="cr-contact-body">
      <span className="cr-contact-label">Phone</span>
      <span className="cr-contact-value cr-contact-value--line">{SCHOOL.phone}</span>
    </div>
  </a>
</section>
      </main>

      {/* ───────── Apply modal ───────── */}
      {activeJob && (
        <div
          className="cr-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeApply();
          }}
        >
          <div className="cr-modal" role="dialog" aria-modal="true" aria-labelledby="cr-modal-title">
            <div className="cr-modal-head">
              <div>
                <h3 id="cr-modal-title">Apply for {activeJob.title}</h3>
                {activeJob.department && <p>{activeJob.department}</p>}
              </div>
              <button type="button" className="cr-close" onClick={closeApply} aria-label="Close">
                <Icon name="close" size={18} />
              </button>
            </div>

            {done ? (
              <div className="cr-success">
                <div className="cr-success-ico">
                  <Icon name="check" size={34} />
                </div>
                <h3>Application received</h3>
                <p>Thank you! Your application has been received. We will contact you soon.</p>
                <button type="button" className="cr-btn cr-btn--navy" onClick={closeApply}>
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="cr-form">
                <div className="cr-form-row">
                  <div className="cr-field">
                    <label htmlFor="cr-name">Full Name <span className="cr-req">*</span></label>
                    <input
                      id="cr-name"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      disabled={submitting}
                      autoComplete="name"
                      required
                    />
                  </div>
                  <div className="cr-field">
                    <label htmlFor="cr-email">Email <span className="cr-req">*</span></label>
                    <input
                      id="cr-email"
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      disabled={submitting}
                      autoComplete="email"
                      required
                    />
                  </div>
                </div>

                <div className="cr-field">
                  <label htmlFor="cr-phone">Phone</label>
                  <input
                    id="cr-phone"
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    disabled={submitting}
                    autoComplete="tel"
                  />
                </div>

                <div className="cr-field">
                  <label htmlFor="cr-message">Message (optional)</label>
                  <textarea
                    id="cr-message"
                    name="message"
                    rows={3}
                    value={form.message}
                    onChange={handleChange}
                    disabled={submitting}
                  />
                </div>

                <div className="cr-field">
                  <span className="cr-label">CV / Resume <span className="cr-req">*</span></span>
                  <label className={`cr-drop ${submitting ? "is-disabled" : ""}`}>
                    <input type="file" accept=".pdf,.doc,.docx" onChange={handleFile} disabled={submitting} />
                    <span className="cr-drop-ico">
                      <Icon name={cvFile ? "doc" : "upload"} size={22} />
                    </span>
                    <span className="cr-drop-text">
                      {cvFile ? (
                        <>
                          <strong>{cvFile.name}</strong>
                          <small>{formatSize(cvFile.size)} · tap to change</small>
                        </>
                      ) : (
                        <>
                          <strong>Choose your CV</strong>
                          <small>PDF, DOC, DOCX · max {MAX_FILE_MB} MB</small>
                        </>
                      )}
                    </span>
                  </label>
                </div>

                {status && <p className={`cr-status cr-status--${status.type}`}>{status.text}</p>}

                <button type="submit" className="cr-submit" disabled={submitting}>
                  {submitting ? "Sending application..." : "Submit Application"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CareersPage;